import {
  EPerformanceEvaluationStatus,
  EPerformanceFormQuestionType,
  EPerformanceFormStatus,
  EPredicateFields,
  EvaluationFormTypes,
} from '@common/definitions';
import { SimpleUser } from '@common/types/objects';
import { PerformanceQuestionType } from './definitions';

export const TypeAttachment = <const> {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  EXTERNAL_VIDEO: 'external_video',
  PDF: 'pdf'
};

export type EvaluationFormPayload = {
  title: string;
  type: EvaluationFormTypes;
  icon: string;
  color: string;
  status: EPerformanceFormStatus;
  settings: {
    employeeVisibility: boolean;
  };
};

export type ContentComponent = {
  id: string;
  type: typeof TypeAttachment[keyof typeof TypeAttachment];
  index: number;
  parameters: {
    text?: string;
    attachment_id?: string;
    [key: string]: any;
  };
};

export type ComponentSettingsPayload = {
  type?: string;
  settings: {
    text?: string;
    description?: string;
    answer_required?: boolean;
    visibility?: {
      manager: boolean;
      employee: boolean;
    };
    competencyIds?: string[];
    content_components?: ContentComponent[];
  } | Array<{
    type: string;
    index: number;
    parameters: Record<string, any>;
  }>;
};

export type Question = {
  type: EPerformanceFormQuestionType;
  index: number;
};

export type PerformanceFormComponent = {
  id: string;
  form_id: string;
  type: EPerformanceFormQuestionType;
  index: number;
  settings: {
    text?: string;
    description?: string;
    visibility?: {
      manager: boolean;
      employee: boolean;
      answer_visible: boolean;
    };
    answer_required?: boolean;
    category_ids?: string[];
    content_components?: ContentComponent[];
    impact_of_loss?: boolean;
    retention_risk?: boolean;
  };
  created_at: 'string';
  updated_at: 'string';
};

export type APIEvaluationForm = {
  id: string;
  organisation_id: string;
  title: string;
  status: EPerformanceFormStatus;
  type: EvaluationFormTypes;
  employee_visibility: boolean;
  created_by: string;
  color?: string;
  icon?: string;
  components: PerformanceFormComponent[];
  created_at: 'string';
  updated_at: 'string';
  archived_at: 'string';
  deleted_at: string | null;
};

export type PerformanceFormStored = Omit<
  APIEvaluationForm,
  'created_by' | 'components'
> & {
  created_by: string;
  component_ids?: string[];
};

export type PerformanceForm = Omit<
  PerformanceFormStored,
  'created_by' | 'component_ids'
> & {
  created_by: SimpleUser;
};

export type PerformanceFormDetail = PerformanceForm & {
  components: PerformanceFormComponent[];
  component_ids?: string[];
};

export type Compenents = APIEvaluationForm & {
  components: PerformanceFormComponent[];
  component_ids?: string[];
  question_id?: string | null;
};

export type FormsData = {
  id: string;
  organisation_id: string;
  title: string;
  status: EPerformanceFormStatus;
  type: EvaluationFormTypes;
  employee_visibility: boolean;
  color: string;
  icon: string;
  created_by: string | SimpleUser;
  components: Array<PerformanceFormComponent>;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  deleted_at: string | null;
};

export type ApiFormsResponse = {
  data: FormsData[];
};

export type FormsPagination = {
  total_count: number;
  next_cursor: string;
};

export type FormsRelated = {
  users: SimpleUser[];
};

export type FormsMeta = {
  pagination: FormsPagination;
  related: FormsRelated;
};

export type PerformanceFormQuestion = {
  type: typeof PerformanceQuestionType[keyof typeof PerformanceQuestionType];
  index: number;
  settings: {
    text: string;
    description: string;
    visibility: {
      manager: boolean;
      employee: boolean;
      answer_visible: boolean;
    };
    answer_required: boolean;
  };
};

export type RatingScalePayload = {
  locale?: string;
  id?: string;
  label?: string;
  description?: string;
  color?: string;
  index?: number;
  competencyId?: string;
};

export type CompetancyPayload = {
  locale?: string;
  name: string;
  description: string;
  categoryId: string;
  functionIds: string[];
  ratingScales?: RatingScalePayload[];
};


export type RatingScale = {
  id: string;
  organisation_id: string;
  competency_id: string;
  label: string;
  description: string;
  index: number;
  color: string;
};


export type Predicates = {
  filter: any;
  value: {
    attribute: EPredicateFields;
    comparison: 'eq';
    value: string;
  };
  options: {
    label: string;
    value: string;
  }[];
}[];

export type CategoryPayload = {
  name: string;
  description?: string;
};

export interface Network {
  id: string;
  name: string;
}

export interface ReviewFunction {
  id: string;
  name: string;
}

export interface Reviewee {
  id: string;
  profile_img: string;
  full_name: string;
  networks: Network[];
  functions: ReviewFunction[];
  status: EPerformanceEvaluationStatus;
  updated_at: string;
  submitted_at: string | null;
}
export interface Reviewer {
  id: string;
  profile_img: string;
  full_name: string;
  status: EPerformanceEvaluationStatus;
  updated_at: string;
  submitted_at: string | null;
}

export type Evaluation = {
  id: string;
  organisation_id: string;
  reviewee: Reviewee;
  reviewers: Reviewer[];
  form: EvaluationFormPayload;
  created_at: string;
  archived_at: string | null;
  deleted_at: string | null;
  completed_at: string | null;
  updated_at?: string | null;
};

export type EvaluationMetaCounts = {
  started: number;
  submitted: number;
  finished: number;
};
export type EvaluationMeta = {
  counts: EvaluationMetaCounts;
  limit: number;
  cursor: string | null;
};

export type EvaluationsResponse = {
  data: Evaluation[];
  meta: EvaluationMeta;
};

export type EvaluationFormDetail = {
  id: string;
  title: string;
  type: EvaluationFormTypes;
  status: EPerformanceFormStatus;
  icon: string;
  employee_visibility: boolean;
  components?: PerformanceFormComponent[];
};

export type EvaluationDetail = {
  id: string;
  organisation_id: string;
  reviewee: Reviewee;
  reviewers: Reviewer[];
  form: EvaluationFormDetail;
  created_at: string;
  deadline_at: string | null;
  archived_at: string | null;
  deleted_at: string | null;
  completed_at: string | null;
  updated_at?: string | null;
  progress?: {
    total_reviewers: number;
    completed_reviewers: number;
    pending_reviewers: number;
  };
  responses?: QuestionResponse[];
  reviewee_competencies?: any;
};

export type QuestionResponse = {
  user_id: string;
  response: [
    {
      type: string;
      component_id: string;
      answer: string;
    }
  ];
};

export type EvaluationDetailResponse = {
  data: EvaluationDetail;
};

export type EvaluationsPagination = {
  limit: number;
  next_cursor: string;
};

export type EvaluationsMeta = {
  pagination: EvaluationsPagination;
  counts: {
    started: number;
    submitted: number;
    completed: number;
    total_count: number;
  };
};


export type PerformanceRatingResponse = {
  total_evaluations: number;
  rating_scales: RatingScaleData[];
};

export type RatingScaleData = {
  id: string;
  label: string;
  description: string;
  color: string;
  index: number;
  total_evaluations: number;
};

export type EmployeeScore = {
  competency_id: string;
  score: number;
};

export type Employee = {
  id: string;
  first_name: string;
  last_name: string;
  profile_img: string;
  functions: string[];
  networks: string[];
  scores: EmployeeScore[];
  average_rating: number;
  evaluation_count: number;
  last_evaluation_at: string;
};

export type Competency = {
  id: string;
  name: string;
  description: string;
  category_id: string;
  category: {
    id: string;
    name: string;
  };
  functions: Array<{
    id: string;
    name: string;
  }>;
};

export type PerformanceReportData = Employee[];

export type PerformanceReportMeta = {
  competencies: Competency[];
  rating_scales: RatingScaleData[];
  pagination: {
    total_count: number;
    limit: number;
    next_cursor: string;
  };
};

export type PerformanceReportResponse = {
  data: PerformanceReportData;
  meta: PerformanceReportMeta;
};

export type Category = {
 data: {
  id: string;
  organisation_id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
 }[];
};


export enum RatingType {
  POTENTIAL = 'potential',
  PERFORMANCE = 'performance',
  RETENTION_RISK = 'retention_risk',
  IMPACT_OF_LOSS = 'impact_of_loss',
}
